import type { Feature } from "@turf/helpers";
import type { FormikProps, FormikValues } from "formik";
import type { Map } from "mapbox-gl";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { getInitialsColor, parseGeoData, useGlobalState } from "senf-shared";
import { useCesiumFeatures } from "../../../../hooks/useCesiumFeatures";
import { cn } from "../../../../util/classNameConstructor";
import { flyToPolygon } from "../../../atoms/map/utils/flyToPolygon";
import Button from "../../../core/button/Button";
import { Combobox, type ComboboxOption } from "../../../core/inputs/Combobox";
import { Input } from "../../../core/inputs/Input";
import Select from "../../../core/select/Select";
import EditableContent from "../components/EditableContent";
import { useDefaultValues, useRandomSorting } from "../hooks";
import MultipleChoice from "../questions/MultipleChoice";
import type { QuestionValue } from "../results.config";
import type {
  InputColumn,
  InputRow,
  Page,
  QuestionBaseProps,
} from "../survey.config";

interface MapInputProps
  extends QuestionBaseProps<"mapPoint" | "mapPath" | "mapArea"> {
  isMobile: boolean;
  statefulMap: Map;
  featureId: string | null;
  setFeatureId: React.Dispatch<React.SetStateAction<string | null>>;
  setOpenDescription: React.Dispatch<React.SetStateAction<boolean>>;
}

const number = ({ properties }) => properties?.number || 0;

const getGeoData = ({ geoData, feature, props }) => {
  const updatedFeatures = JSON.parse(geoData)?.features.map((f, i) =>
    i === (feature?.properties?.number || 0) - 1
      ? {
          ...f,
          properties: {
            ...f?.properties,
            ...props,
          },
        }
      : f,
  );
  return {
    geoData: JSON.stringify({
      ...JSON.parse(geoData),
      features: updatedFeatures,
    }),
  };
};
const getFeature = ({ isMobile, geoData, index, featureId }) => {
  return isMobile
    ? parseGeoData(geoData)?.features?.find(({ id }) => id === featureId)
    : parseGeoData(geoData)?.features?.[index];
};
const getSelected = ({ isMobile, columns, geoData, index, featureId }) => {
  return columns.find(
    (item) =>
      item.uid ===
      getFeature({ isMobile, geoData, index, featureId })?.properties?.choices,
  );
};
interface MapInputElementProps extends MapInputProps {
  geoData: string;
  index: number;
  feature: Feature;
}
interface MapDropdownProps extends MapInputElementProps {
  columns: InputColumn<"mapPoint" | "mapPath" | "mapArea">[];
  defaultQDropdownLabel: string;
  handleSaveDrawn: (features: Feature[]) => void;
}
interface MapTextAreaProps extends MapInputElementProps {
  defaultTextTitle: string;
  handleFly: (feature: Feature) => void;
  minChar?: number;
  maxChar?: number;
  isCesium?: boolean;
}
const MapInputDropdown = ({
  page,
  mode,
  geoData,
  index,
  feature,
  isMobile,
  columns,
  setFeatureId,
  onChange,
  featureId,
  setOpenDescription,
  statefulMap,
  defaultQDropdownLabel,
  handleSaveDrawn,
  leadingIcon,
}: MapDropdownProps) => {
  const [error, setError] = useState<boolean>();
  const [errorCorrectionInProgress, setErrorCorrectionInProgress] =
    useState<boolean>();
  const [translation] = useGlobalState().translationsState;

  // useEffect(() => {
  //   if (error !== undefined) setError(!feature.properties?.choices?.length);
  // }, [feature.properties?.choices]);

  // const windowClick = () => {
  //   setError(
  //     !errorCorrectionInProgress && !feature.properties?.choices?.length
  //   );
  // };

  // useEffect(() => {
  //   setTimeout(() => {
  //     window.addEventListener("click", windowClick);
  //   }, 100);
  //   return () => window.removeEventListener("click", windowClick);
  // }, [errorCorrectionInProgress]);

  const handleMapClick = () => {
    setError(
      !errorCorrectionInProgress && !feature.properties?.choices?.length,
    );
  };

  useEffect(() => {
    if (statefulMap) {
      statefulMap.on("click", handleMapClick);
      statefulMap.on("touchstart", handleMapClick);
    }
    return () => {
      if (statefulMap) {
        statefulMap.off("click", handleMapClick);
        statefulMap.off("touchstart", handleMapClick);
      }
    };
  }, [errorCorrectionInProgress, statefulMap]);

  const selectedValue = getSelected({
    isMobile,
    columns,
    geoData,
    index,
    featureId,
  })?.label;

  const handleChange = (option: ComboboxOption) => {
    // const handleChange = (value: string) => {
    const { value } = option;
    if (!value) return;
    setErrorCorrectionInProgress(true);
    const color = getInitialsColor(
      value === "other"
        ? "other"
        : page.options?.columns?.findIndex((item) => item.uid === value),
      "css",
    );

    onChange?.(
      getGeoData({
        geoData,
        feature,
        props: {
          choices: value,
          color,
        },
      }),
    );

    // Update the feature's properties directly
    statefulMap?.df?.setFeatureProperty(feature.id, "user_color", color);

    statefulMap?.df?.changeMode("simple_select");

    if (!isMobile) {
      setFeatureId(null);
      setOpenDescription(false);
    }
    setError(false);
  };

  if (page?.settings.valueType !== "boolean") {
    return null;
  }

  return (
    <Combobox
      placeholder={
        (translation?.[page.settings.mapResponse.placeholder || ""] ||
          page.settings.mapResponse.placeholder) ??
        defaultQDropdownLabel
      }
      enableSearch={false}
      options={columns?.filter(Boolean).map((option) => ({
        label: translation?.[option.label] || option.label,
        value: option.uid,
      }))}
      value={translation?.[selectedValue] || selectedValue}
      leadingIcon={leadingIcon}
      onChange={handleChange}
    />
  );
};

const TextArea = ({
  page,
  feature,
  handleFly,
  geoData,
  isMobile,
  maxChar,
  minChar,
  onChange,
  defaultTextTitle,
  statefulMap,
  setOpenDescription,
  handleSaveDrawn,
  isCesium,
}: MapTextAreaProps) => {
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;
  const { updateFeatureProperties, getSavedFeatures } = useCesiumFeatures();
  const [translation] = useGlobalState().translationsState;
  const [error, setError] = useState<boolean>();
  const [errorCorrectionInProgress, setErrorCorrectionInProgress] =
    useState<boolean>();

  // useEffect(() => {
  //   if (error !== undefined) setError(!feature.properties?.description?.length);
  // }, [feature.properties?.description]);

  // const windowClick = () => {
  //   setError(
  //     !errorCorrectionInProgress && !feature.properties?.description?.length
  //   );
  // };

  // useEffect(() => {
  //   setTimeout(() => {
  //     window.addEventListener("click", windowClick);
  //   }, 100);
  //   return () => window.removeEventListener("click", windowClick);
  // }, [errorCorrectionInProgress]);

  const handleMapClick = () => {
    setError(
      !errorCorrectionInProgress && !feature.properties?.description?.length,
    );
  };

  useEffect(() => {
    if (statefulMap) {
      statefulMap.on("click", handleMapClick);
      statefulMap.on("touchstart", handleMapClick);
    }
    return () => {
      if (statefulMap) {
        statefulMap.off("click", handleMapClick);
        statefulMap.off("touchstart", handleMapClick);
      }
    };
  }, [errorCorrectionInProgress, statefulMap]);

  return (
    <div className="relative w-full">
      {/* <Button
        leadingIcon="trash"
        size="sm"
        classNameWrapper="absolute top-1 right-1"
        variant="shapeless"
      /> */}
      <Input
        key={(feature?.properties?.number || 0) - 1}
        name={`surveyQuestionInput-${feature.id}`} // Update the name to include the index
        // type={questionType}
        type={isMobile ? "input" : "textarea"}
        placeholder={
          translation?.[page.textTitle] || page.textTitle || defaultTextTitle
        }
        onChange={(event) => {
          onChange?.(
            getGeoData({
              geoData,
              feature,
              props: { description: event.target.value },
            }),
          );

          // Update Cesium saved features if it's a Cesium map
          if (isCesium) {
            updateFeatureProperties(feature.id || feature.properties?.id, {
              description: event.target.value,
            });
          }

          setErrorCorrectionInProgress(
            !feature.properties?.description?.length,
          );
          setError(!feature.properties?.description?.length);

          if (feature.properties?.description?.length) {
            setOpenDescription(false);
          }
        }}
        onBlur={() => {
          setErrorCorrectionInProgress(
            !feature.properties?.description?.length,
          );
          setError(!feature.properties?.description?.length);

          if (feature.properties?.description?.length) {
            setOpenDescription(false);
          }
        }}
        onFocus={() => {
          if (isCesium) {
            const currentFeatures = getSavedFeatures();
            if (currentFeatures) {
              handleSaveDrawn(currentFeatures);
            }
            return;
          }
          statefulMap?.df?.changeMode("simple_select");
          handleSaveDrawn(statefulMap?.df?.getAll());
          handleFly(feature);
          setErrorCorrectionInProgress(true);
        }}
        error={error === true && !feature.properties?.description?.length}
        columns={isMobile ? 3 : 1}
        maxRows={10}
        minLength={minChar}
        maxLength={maxChar}
        value={feature.properties?.description} // Update the value to access the description property
      />
    </div>
  );
};
type SimpleMapInputProps = {
  page: Page<"mapPoint" | "mapPath" | "mapArea">;
  isMobile: boolean;
  features: Feature[];
  handleSaveDrawn: (features: Feature[]) => void;
  handleFly: (feature: Feature) => void;
  onChange: (value: Partial<QuestionValue>) => void;
  formikValues: FormikValues;
  formik: FormikProps<FormikValues>;
  columns: InputColumn<"mapPoint" | "mapPath" | "mapArea">[];
  mode: "display" | "edit";
  defaultMapSelectDescription: string;
  defaultTextTitle: string;
  setFeatureId: (featureId: string) => void;
  featureId: string;
  setOpenDescription: (openDescription: boolean) => void;
  statefulMap: Map;
  isCesium?: boolean;
};
const SimpleMapInput = ({
  page,
  isMobile,
  features,
  handleSaveDrawn,
  handleFly,
  onChange,
  formikValues,
  formik,
  columns,
  mode,
  defaultMapSelectDescription,
  defaultTextTitle,
  setFeatureId,
  featureId,
  setOpenDescription,
  statefulMap,
  isCesium,
}: SimpleMapInputProps) => {
  const [translation] = useGlobalState().translationsState;

  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;
  const { removeFeature } = useCesiumFeatures();
  const mapPage =
    page.type === "mapPoint" ||
    page.type === "mapPath" ||
    page.type === "mapArea";
  if (!mapPage || page?.settings.valueType === "none") return;

  const elementProps = {
    page,
    isMobile,
    onChange,
    geoData: formikValues?.geoData,
    formik,
  };
  return (
    <div
      className={cn(
        "flex flex-col gap-5",
        isMobile &&
          features?.length &&
          "-order-1 p-5 border-b border-border-subdued",
      )}
    >
      {features?.map((feature, index) => {
        return (
          <div
            key={`feature-${number(feature) - 1}`}
            // className={!isMobile && "mt-5"}
          >
            {/* <div
              className="body-sm-bold absolute z-10 -ml-1 -mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white"
              style={{
                backgroundColor: feature.properties?.color || "#CD992E",
              }}
            >
              {number(feature)}
            </div> */}

            {page?.settings.valueType === "boolean" && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <MapInputDropdown
                    {...elementProps}
                    columns={columns}
                    index={index}
                    feature={feature}
                    mode={mode}
                    setFeatureId={setFeatureId}
                    featureId={featureId}
                    setOpenDescription={setOpenDescription}
                    statefulMap={statefulMap}
                    defaultQDropdownLabel={defaultMapSelectDescription}
                    handleSaveDrawn={handleSaveDrawn}
                    leadingIcon={
                      <div
                        className="body-sm-bold flex !h-5 !w-5 shrink-0 items-center justify-center rounded-full text-white"
                        style={{
                          backgroundColor:
                            feature.properties?.color || "#CD992E",
                        }}
                      >
                        {number(feature)}
                      </div>
                    }
                  />
                  {!isMobile && (
                    <Button
                      leadingIcon="trash"
                      size="md"
                      textSubdued
                      // classNameWrapper="absolute top-1 right-1"
                      variant="shapeless"
                      onClick={() => {
                        if (isCesium) {
                          // Use streamlined feature management
                          removeFeature(feature.id || feature.properties?.id, {
                            handleSaveDrawn,
                          });
                          setOpenDescription(false);
                          return;
                        }
                        statefulMap?.df?.delete(feature.id);
                        setOpenDescription(false);
                        handleSaveDrawn(statefulMap?.df?.getAll());
                      }}
                    />
                  )}
                </div>
                {page.settings.otherOption?.enabled &&
                  getSelected({
                    isMobile,
                    columns,
                    geoData: elementProps.geoData,
                    index,
                    featureId,
                  })?.uid === "other" && (
                    <Input
                      key={(feature?.properties?.number || 0) - 1}
                      name={`surveyQuestionInput-${feature.id}`} // Update the name to include the index
                      // type={questionType}
                      placeholder={
                        translation?.[page.settings.otherOption?.label || ""] ||
                        page.settings.otherOption?.label ||
                        defaultTextTitle
                      }
                      onChange={(event) => {
                        onChange?.(
                          getGeoData({
                            geoData: elementProps.geoData,
                            feature,
                            props: { comment: event.target.value },
                          }),
                        );
                      }}
                      value={feature.properties?.comment} // Update the value to access the description property
                    />
                  )}
              </div>
            )}
            {page?.settings.valueType === "string" && (
              <div className="flex items-center gap-2">
                <TextArea
                  {...elementProps}
                  handleFly={handleFly}
                  minChar={page?.settings.limitCharacters?.minChars}
                  maxChar={page?.settings.limitCharacters?.maxChars}
                  defaultTextTitle={defaultTextTitle}
                  feature={feature}
                  statefulMap={statefulMap}
                  setOpenDescription={setOpenDescription}
                  handleSaveDrawn={handleSaveDrawn}
                  isCesium={isCesium}
                />
                {!isMobile && (
                  <Button
                    leadingIcon="trash"
                    size="md"
                    // classNameWrapper="absolute top-1 right-1"
                    variant="shapeless"
                    textSubdued
                    onClick={() => {
                      if (isCesium) {
                        // Use streamlined feature management
                        removeFeature(feature.id || feature.properties?.id, {
                          handleSaveDrawn,
                        });
                        setOpenDescription(false);
                        return;
                      }
                      statefulMap?.df?.delete(feature.id);
                      setOpenDescription(false);
                      handleSaveDrawn(statefulMap?.df?.getAll());
                    }}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const MapInput = ({
  page,
  formikValues,
  updateQuestion,
  mode,
  featureId,
  setFeatureId,
  statefulMap,
  removeItem,
  addItem,
  updateText,
  generateItems,
  isMobile,
  setOpenDescription,
  onChange,
  formik,
  handleSaveDrawn,
  mapData,
  ...props
}: MapInputProps) => {
  const isCesium = mapData?.mapStyle === "cesium";
  const [translation] = useGlobalState().translationsState;
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;

  const { defaultMapSelectDescription, defaultOtherTitle, defaultTextTitle } =
    useDefaultValues(page);

  const handleFly = (feature: Feature) => {
    if (feature.geometry.type === "Point") {
      return;
    }
    flyToPolygon(
      statefulMap,
      {
        type: "FeatureCollection",
        features: [feature],
      },
      {
        duration: 1000,
        padding: isMobile
          ? { top: 50, bottom: 300, left: 50, right: 50 }
          : { top: 50, bottom: 50, left: 50, right: 50 },
      },
    );
  };
  const geodata = formikValues?.geoData && JSON.parse(formikValues?.geoData);
  const features = geodata?.features?.filter((feature) =>
    isMobile ? feature.id === featureId : true,
  );

  console.log("page", formikValues);

  const sortedOptions = useRandomSorting<
    "mapPoint" | "mapPath" | "mapArea",
    "columns"
  >(page, "columns", mode);

  const otherOption = useMemo<
    InputColumn<"mapPoint" | "mapPath" | "mapArea"> | undefined
  >(() => {
    if (
      page.settings.valueType !== "boolean" ||
      !page.settings.otherOption?.enabled
    )
      return undefined;
    return {
      uid: "other",
      label:
        (translation?.[page.settings.otherOption?.label || ""] ||
          page.settings.otherOption?.label) ??
        props.defaultOtherTitle,
    };
  }, [
    page.settings.valueType,
    page.settings.otherOption?.label,
    page.settings.otherOption?.enabled,
    translation,
  ]);

  const columns = useMemo(() => {
    return mode === "display" && otherOption
      ? [...sortedOptions, otherOption]
      : sortedOptions;
  }, [sortedOptions, otherOption, mode]);

  useEffect(() => {
    if (features?.length && !isMobile) {
      const element = document.getElementById("surveyWrapper");
      if (element) {
        // Scroll to the bottom of the element
        element.scrollTo({
          top: element.scrollHeight, // Use scrollHeight to scroll to the bottom
          left: 0,
          behavior: "smooth", // Optional: for smooth scrolling
        });
      }
    }
  }, [features?.length, isMobile]);
  const fallbackRef = useRef<HTMLDivElement>(null);
  if (mode === "display") {
    return (
      <div
        ref={fallbackRef}
        className={cn(
          isMobile && features?.length
            ? "absolute z-50 bottom-0 ps-5 pe-16 pb-7 flex gap-2 w-full pointer-events-none"
            : "",
        )}
      >
        {ReactDOM.createPortal(
          <SimpleMapInput
            page={page}
            isMobile={isMobile}
            features={features}
            handleSaveDrawn={handleSaveDrawn}
            handleFly={handleFly}
            onChange={(value) => onChange?.(value)}
            formikValues={formikValues}
            formik={formik}
            columns={columns}
            mode={mode}
            defaultMapSelectDescription={defaultMapSelectDescription}
            defaultTextTitle={defaultTextTitle}
            setFeatureId={setFeatureId}
            featureId={featureId}
            setOpenDescription={setOpenDescription}
            statefulMap={statefulMap}
            isCesium={isCesium}
          />,
          (isMobile &&
            features?.length &&
            document.getElementById("survey-footer-container")) ||
            fallbackRef.current ||
            document.body,
        )}
      </div>
    );
  }

  if (page.settings.valueType === "string") {
    return (
      <EditableContent
        defaultValue={props.defaultTextTitle}
        value={page.settings.limitCharacters?.placeholder}
        onChange={(e) =>
          updateQuestion(
            {
              ...page.settings,
              limitCharacters: {
                ...((page as Page<"mapPoint" | "mapPath" | "mapArea", "string">)
                  .settings.limitCharacters || {}),
                placeholder: e.target.value ?? null,
              },
            },
            "settings",
          )
        }
        columns={1}
        mode={mode}
        silent={false}
      />
    );
  }
  <span>{page.settings.valueType}</span>;
  if (page.settings.valueType === "boolean") {
    return (
      <div className="flex flex-col">
        <div className="flex h-full w-full flex-col gap-4">
          {/** biome-ignore lint/nursery/useUniqueElementIds: <explanation> */}
          <EditableContent
            id="qDropdownLabel"
            className="body-md"
            value={page.settings.mapResponse.placeholder}
            defaultValue={defaultMapSelectDescription}
            onChange={(e) =>
              updateQuestion(
                {
                  ...page.settings,
                  mapResponse: {
                    ...page.settings.mapResponse,
                    placeholder: e.target.value,
                  },
                },
                "settings",
              )
            }
            maxRows={18}
            placeholder={defaultMapSelectDescription}
            mode={mode}
          />
          <MultipleChoice
            value={formikValues?.value}
            onChange={onChange}
            mode={mode}
            updateQuestion={updateQuestion}
            removeItem={removeItem}
            addItem={addItem}
            generateItems={generateItems}
            updateItemText={props.updateItemText}
            updateOtherOption={props.updateOtherOption}
            defaultTextTitle={defaultTextTitle}
            defaultOtherTitle={defaultOtherTitle}
            page={page}
            {...page}
            // otherOption={
            //   translation?.[page.mapbasedOtherOption] ||
            //   page.mapbasedOtherOption
            // }
          />
        </div>
      </div>
    );
  }
  return "null";
};
MapInput.displayName = "MapInput";
export default MapInput;
